import type { VentureLabel } from './methodology';

// ── Label → Color mapping ──

export function labelColor(label: VentureLabel | string): string {
  switch (label) {
    case 'excellent':
    case 'strong':
      return '#22c55e';
    case 'moderate':
      return '#f59e0b';
    case 'weak':
    case 'critical':
      return '#ef4444';
    default:
      return '#888888';
  }
}

export function labelBgColor(label: VentureLabel | string): string {
  switch (label) {
    case 'excellent':
    case 'strong':
      return 'rgba(34,197,94,0.06)';
    case 'moderate':
      return 'rgba(245,158,11,0.06)';
    case 'weak':
    case 'critical':
      return 'rgba(239,68,68,0.06)';
    default:
      return 'rgba(255,255,255,0.04)';
  }
}

export function labelBorderColor(label: VentureLabel | string): string {
  switch (label) {
    case 'weak':
    case 'critical':
      return 'rgba(239,68,68,0.12)';
    default:
      return 'transparent';
  }
}

// ── Evidence level mapping ──

export type EvidenceLabel = 'High' | 'Moderate' | 'Low';

export function evidenceLevelToLabel(level: string): EvidenceLabel {
  switch (level) {
    case 'verified':
      return 'High';
    case 'partially-verified':
      return 'Moderate';
    case 'unverified':
    default:
      return 'Low';
  }
}

export function evidenceLabelColor(label: EvidenceLabel): string {
  switch (label) {
    case 'High': return '#22c55e';
    case 'Moderate': return '#f59e0b';
    case 'Low': return '#ef4444';
  }
}

// ── Evidence source type (4-tier — for dimension-level detail) ──

export type EvidenceSource = 'None' | 'Self-declared' | '3rd Party' | 'Verified';

/**
 * Map server `evidenceLevel` (3-tier verification) → 4-tier source.
 * Heuristic until LLM emits source directly (Phase 102+).
 */
export function evidenceLevelToSource(
  level: string | undefined | null,
  hasFeedback: boolean = true,
): EvidenceSource {
  if (!level || !hasFeedback) return 'None';
  switch (level) {
    case 'verified':
      return 'Verified';
    case 'partially-verified':
      return '3rd Party';
    case 'unverified':
    default:
      return 'Self-declared';
  }
}

export function evidenceSourceColor(source: EvidenceSource, colors: any): string {
  switch (source) {
    case 'None': return colors.error;
    case 'Self-declared': return colors.warning;
    case '3rd Party': return colors.info;
    case 'Verified': return colors.success;
  }
}

// ── Confidence aggregation (for scorecard) ──

/**
 * Source mix flavor — short tagline describing the evidence-source distribution.
 * Returns e.g. "mostly verified", "mixed sources", "self-claim heavy".
 */
export function evidenceSourceMixFlavor(sources: EvidenceSource[]): string {
  if (sources.length === 0) return '';
  const counts = sources.reduce((acc, s) => {
    acc[s] = (acc[s] ?? 0) + 1;
    return acc;
  }, {} as Record<EvidenceSource, number>);
  const total = sources.length;
  const verifiedPct = (counts['Verified'] ?? 0) / total;
  const thirdPartyPct = (counts['3rd Party'] ?? 0) / total;
  const selfPct = (counts['Self-declared'] ?? 0) / total;
  const nonePct = (counts['None'] ?? 0) / total;

  if (verifiedPct >= 0.6) return 'mostly verified';
  if (verifiedPct + thirdPartyPct >= 0.6) return 'verified & cited';
  if (selfPct >= 0.5) return 'self-claim heavy';
  if (nonePct >= 0.4) return 'sparse evidence';
  return 'mixed sources';
}

/**
 * Confidence band labels — for "Strong · 87%" style display.
 */
export function confidenceBandLabel(confidence: number): 'Weak' | 'Moderate' | 'Strong' {
  if (confidence >= 75) return 'Strong';
  if (confidence >= 50) return 'Moderate';
  return 'Weak';
}

export function confidenceBandColor(confidence: number, colors: any): string {
  if (confidence >= 75) return colors.success;
  if (confidence >= 50) return colors.warning;
  return colors.error;
}

// ── Verification status mapping (data-room claims — Phase 116.7-04) ──

export type VerificationStatus =
  | 'verified'
  | 'contradicted'
  | 'unverifiable'
  | 'skipped'
  | 'unchecked';

export type ClaimBadgeTone = 'success' | 'error' | 'warning' | 'muted';

/**
 * Map a claim's denormalized `verification_status` → a display badge.
 * Tones map onto the existing theme palette (no new colors):
 *   verified     → success (green)
 *   contradicted → error (red)
 *   unverifiable → warning (amber)
 *   skipped      → muted
 *   unchecked    → muted
 */
export function verificationStatusToBadge(
  status: VerificationStatus | string,
): { label: string; tone: ClaimBadgeTone } {
  switch (status) {
    case 'verified':
      return { label: 'Verified', tone: 'success' };
    case 'contradicted':
      return { label: 'Contradicted', tone: 'error' };
    case 'unverifiable':
      return { label: 'Unverifiable', tone: 'warning' };
    case 'skipped':
      return { label: 'Skipped', tone: 'muted' };
    case 'unchecked':
    default:
      return { label: 'Unchecked', tone: 'muted' };
  }
}

/** Resolve a claim badge tone → theme color (text/icon color). */
export function claimBadgeToneColor(tone: ClaimBadgeTone, colors: any): string {
  switch (tone) {
    case 'success':
      return colors.success;
    case 'error':
      return colors.error;
    case 'warning':
      return colors.warning;
    case 'muted':
    default:
      return colors.text.tertiary;
  }
}

/** Resolve a claim badge tone → theme background color (soft tinted pill). */
export function claimBadgeToneBgColor(tone: ClaimBadgeTone, colors: any): string {
  switch (tone) {
    case 'success':
      return colors.traffic.greenLight;
    case 'error':
      return colors.red.light;
    case 'warning':
      return colors.amber.light;
    case 'muted':
    default:
      return colors.surface.secondary;
  }
}

// ── Risk level mapping ──

export function riskLabelColor(level: string): string {
  switch (level) {
    case 'high': return '#ef4444';
    case 'medium': return '#f59e0b';
    case 'low':
    case 'none':
    default: return '#22c55e';
  }
}

export function riskLevelToDisplay(level: string): string {
  switch (level) {
    case 'high': return 'High';
    case 'medium': return 'Moderate';
    case 'low':
    case 'none': return 'Low';
    default: return 'Low';
  }
}

// ── Label display ──

export function capitalizeLabel(label: string): string {
  return label.charAt(0).toUpperCase() + label.slice(1);
}

// ── Venture label from score (client-side mirror of server scoreToVentureLabel) ──

export function scoreToLabel(score: number): VentureLabel {
  if (score >= 81) return 'excellent';
  if (score >= 66) return 'strong';
  if (score >= 46) return 'moderate';
  if (score >= 26) return 'weak';
  return 'critical';
}

// ── Pillar grouping ──

// B6: must mirror the server's pillar membership
// (_shared/scoring/methodology/v2/weights.ts FUNDAMENTAL_DIMENSIONS / GROWTH_DIMENSIONS).
// deal-attractiveness + social-proof are FUNDAMENTALS (ventureQuality axis) on the
// server; the client previously filed them under Growth, so the Fundamentals average
// shown on the founder score page, investor detail, and PDF excluded two dimensions
// the server's axis includes.
export const FUNDAMENTALS_DIMS = [
  'team', 'product', 'market', 'defensibility',
  'competition', 'business-model', 'vision-story',
  'deal-attractiveness', 'social-proof',
] as const;

export const GROWTH_DIMS = [
  'traction', 'timing', 'gtm-execution', 'financial-health',
  'execution-velocity', 'scalability',
] as const;

export interface PillarSummary {
  name: string;
  dimensions: string[];
  averageScore: number;
  label: VentureLabel;
}

/**
 * Accepts either the legacy `{ dimension, rawScore }` shape OR the v2 atom
 * shape `{ id, rawScore }`. Phase 116.6 onward, the atom shape is canonical.
 */
export function computePillarSummaries(
  dimensions: Array<{ dimension?: string; id?: string; rawScore: number }>
): { fundamentals: PillarSummary; growth: PillarSummary } {
  const idOf = (d: { dimension?: string; id?: string }) => d.id ?? d.dimension ?? '';
  const avg = (dims: readonly string[]) => {
    const matching = dimensions.filter((d) => (dims as readonly string[]).includes(idOf(d)));
    if (matching.length === 0) return 0;
    return Math.round(matching.reduce((sum, d) => sum + d.rawScore, 0) / matching.length);
  };

  const fundAvg = avg(FUNDAMENTALS_DIMS);
  const growthAvg = avg(GROWTH_DIMS);

  return {
    fundamentals: {
      name: 'Fundamentals',
      dimensions: [...FUNDAMENTALS_DIMS],
      averageScore: fundAvg,
      label: scoreToLabel(fundAvg),
    },
    growth: {
      name: 'Growth & Execution',
      dimensions: [...GROWTH_DIMS],
      averageScore: growthAvg,
      label: scoreToLabel(growthAvg),
    },
  };
}

// ── Theme-aware color functions ──
// Use these in components where useThemeColors() is available

export function labelThemeColor(label: string, colors: any): string {
  switch (label) {
    case 'excellent':
    case 'strong':
      return colors.success;
    case 'moderate':
      return colors.warning;
    case 'weak':
    case 'critical':
      return colors.error;
    default:
      return colors.text.secondary;
  }
}

export function labelThemeBgColor(label: string, colors: any): string {
  switch (label) {
    case 'excellent':
    case 'strong':
      return colors.traffic.greenLight;
    case 'moderate':
      return colors.amber.light;
    case 'weak':
    case 'critical':
      return colors.red.light;
    default:
      return colors.surface.secondary;
  }
}

export function labelThemeBorderColor(label: string, colors: any): string {
  switch (label) {
    case 'weak':
    case 'critical':
      return colors.red.muted;
    default:
      return colors.border.light;
  }
}

export function evidenceThemeColor(label: EvidenceLabel, colors: any): string {
  switch (label) {
    case 'High': return colors.success;
    case 'Moderate': return colors.warning;
    case 'Low': return colors.error;
  }
}

export function riskThemeColor(level: string, colors: any): string {
  switch (level) {
    case 'high': return colors.error;
    case 'medium': return colors.warning;
    case 'low':
    case 'none':
    default: return colors.success;
  }
}

// ── Ring progress calculation ──

export function ringStrokeDasharray(score: number, radius: number = 52): string {
  const circumference = 2 * Math.PI * radius;
  const progress = (score / 100) * circumference;
  return `${Math.round(progress)} ${Math.round(circumference)}`;
}
